import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { CancellationStats } from '@/components/admin/CancellationStats';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Eye, Search } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';
import { useToast } from '@/hooks/use-toast';
import { Skeleton } from '@/components/ui/skeleton';
import { getFullName } from '@/utils/name-helpers';
import BookingDetailModal from '@/components/admin/BookingDetailModal';

// Update the Booking interface to use first_name and last_name instead of full_name
interface Booking {
  id: string;
  start_time: string;
  end_time: string;
  status: string;
  format: string;
  payment_status: string;
  price_at_booking: number;
  currency_at_booking: string;
  client_notes?: string;
  practitioner_notes?: string;
  location_details?: string;
  refund_status?: string;
  refund_reason?: string;
  stripe_session_id?: string;
  stripe_payment_intent_id?: string;
  session_instructions?: string;
  created_at: string;
  updated_at?: string;
  client: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    avatar_url?: string | null;
    email?: string;
  };
  practitioner: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    avatar_url?: string | null;
    professional_title?: string;
  };
  service: {
    id: string;
    service_name: string;
  };
}

const AdminBookings: React.FC = () => {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [page, setPage] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const { toast } = useToast();
  
  const pageSize = 10;

  useEffect(() => {
    fetchBookings();
  }, [page, searchQuery, statusFilter]);

  // Update the fetchBookings function to map the response data correctly
  const fetchBookings = async () => {
    try {
      setLoading(true);
    
      // Build query for bookings table
      let query = supabase
        .from('bookings')
        .select(`
          *,
          client:client_id(id, first_name, last_name, avatar_url),
          practitioner:practitioner_id(id, first_name, last_name, avatar_url, professional_title),
          service:service_id(id, service_name)
        `, { count: 'exact' });

      // Apply filters
      if (searchQuery) {
        query = query.or(`client_notes.ilike.%${searchQuery}%,practitioner_notes.ilike.%${searchQuery}%`);
      }
      
      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }

      // Add pagination
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;
      
      const { data, count, error } = await query
        .order('start_time', { ascending: false })
        .range(from, to);

      if (error) throw error;
      
      // Add a check if the response has data
      if (data) {
        setBookings(data);
        if (count !== null) setTotalCount(count);
      }
    
  } catch (error) {
      console.error('Error fetching bookings:', error);
      toast({
        variant: 'destructive',
        title: '获取预约数据失败',
        description: '请稍后再试',
      });
    } finally {
      setLoading(false);
    }
  };

  // Update the formatClientName function to use first_name and last_name
  const formatClientName = (client: Booking['client']) => {
    if (!client) return 'Unknown Client';
    return getFullName(client) || 'Unnamed Client';
  };

  // Update the formatPractitionerName function
  const formatPractitionerName = (practitioner: Booking['practitioner']) => {
    if (!practitioner) return 'Unknown Practitioner';
    return getFullName(practitioner) || 'Unnamed Practitioner';
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'yyyy-MM-dd HH:mm');
    } catch (error) {
      return 'Invalid date';
    }
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Booking Management</h2>
        <div className="text-sm text-gray-500">Total: {totalCount} bookings</div>
      </div>

      <Tabs defaultValue="bookings" className="space-y-6">
        <TabsList>
          <TabsTrigger value="bookings">Bookings List</TabsTrigger>
          <TabsTrigger value="cancellations">Cancellation Statistics</TabsTrigger>
        </TabsList>

        <TabsContent value="bookings">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Booking List</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search client or practitioner notes..."
                className="pl-8"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <Select
              value={statusFilter}
              onValueChange={setStatusFilter}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="confirmed">Confirmed</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <div className="space-y-2">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : (
            <>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Booking ID</TableHead>
                      <TableHead>Client</TableHead>
                      <TableHead>Practitioner</TableHead>
                      <TableHead>Service</TableHead>
                      <TableHead>Start Time</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Payment Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {bookings.length > 0 ? (
                      bookings.map((booking) => (
                        <TableRow key={booking.id}>
                          <TableCell className="font-mono text-xs">{booking.id.substring(0, 8)}...</TableCell>
                          <TableCell>{formatClientName(booking.client)}</TableCell>
                          <TableCell>{formatPractitionerName(booking.practitioner)}</TableCell>
                          <TableCell>{booking.service?.service_name || <span className="text-gray-400">Not set</span>}</TableCell>
                          <TableCell>{formatDate(booking.start_time)}</TableCell>
                          <TableCell>
                            <Badge variant="secondary">{booking.status}</Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{booking.payment_status}</Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex space-x-2">
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => setSelectedBooking(booking)}
                              >
                                <Eye className="h-3.5 w-3.5 mr-1" />
                                Details
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={8} className="h-24 text-center">
                          No booking data found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>

              {totalPages > 1 && (
                <div className="mt-4">
                  <Pagination>
                    <PaginationContent>
                      <PaginationItem>
                        <PaginationPrevious 
                          onClick={() => setPage(prev => Math.max(prev - 1, 1))}
                          className={page <= 1 ? 'pointer-events-none opacity-50' : ''}
                        />
                      </PaginationItem>
                      
                      {[...Array(totalPages)].map((_, i) => {
                        const pageNumber = i + 1;
                        if (
                          pageNumber === 1 ||
                          pageNumber === totalPages ||
                          (pageNumber >= page - 1 && pageNumber <= page + 1)
                        ) {
                          return (
                            <PaginationItem key={i}>
                              <PaginationLink
                                isActive={pageNumber === page}
                                onClick={() => setPage(pageNumber)}
                              >
                                {pageNumber}
                              </PaginationLink>
                            </PaginationItem>
                          );
                        } else if (
                          (pageNumber === page - 2 && pageNumber > 1) ||
                          (pageNumber === page + 2 && pageNumber < totalPages)
                        ) {
                          return <PaginationItem key={i}>...</PaginationItem>;
                        }
                        return null;
                      })}
                      
                      <PaginationItem>
                        <PaginationNext 
                          onClick={() => setPage(prev => Math.min(prev + 1, totalPages))}
                          className={page >= totalPages ? 'pointer-events-none opacity-50' : ''}
                        />
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <BookingDetailModal
        booking={selectedBooking}
        isOpen={!!selectedBooking}
        onClose={() => setSelectedBooking(null)}
        onRefund={() => {
          fetchBookings();
          setSelectedBooking(null);
        }}
      />
        </TabsContent>

        <TabsContent value="cancellations">
          <CancellationStats />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminBookings;